import { useCallback, useState } from "react";

import { useNewPasswordError } from "../../../_shared/use-new-password-error";
import {
	authInvitationCompanySignUpInputZodSchema,
	type AuthInvitationCompanySignUpInputType,
} from "../_schema/validation-schema";

import type { SafeParseError } from "zod";

const DEFAULT_VALUES: AuthInvitationCompanySignUpInputType = {
	email: "",
	password: "",
};

export const useInvitationCompanySignUpForm = () => {
	const [invitationCompanySignUpForm, setInvitationCompanySignUpForm] =
		useState<AuthInvitationCompanySignUpInputType>(DEFAULT_VALUES);

	const [validationErrors, setValidationErrors] = useState<
		SafeParseError<AuthInvitationCompanySignUpInputType> | undefined
	>(undefined);

	const errors = validationErrors?.error.formErrors.fieldErrors;

	const newPassword = useNewPasswordError({
		password: invitationCompanySignUpForm.password,
		email: invitationCompanySignUpForm.email,
		formatErrorMessage: errors?.password?.at(0),
	});

	const disabled =
		invitationCompanySignUpForm.email === "" ||
		invitationCompanySignUpForm.password === "" ||
		validationErrors !== undefined ||
		!newPassword.isSubmittable;

	const onChangeValidationErrors = useCallback(
		(errors: SafeParseError<AuthInvitationCompanySignUpInputType> | undefined) => {
			setValidationErrors(errors);
		},
		[],
	);

	const onChangeInvitationCompanySignUpForm = useCallback(
		<K extends keyof AuthInvitationCompanySignUpInputType>(
			key: K,
			value: AuthInvitationCompanySignUpInputType[K],
		) => {
			setInvitationCompanySignUpForm((prev) => {
				const zodErrors = authInvitationCompanySignUpInputZodSchema.safeParse({ ...prev, [key]: value });
				onChangeValidationErrors(zodErrors.success ? undefined : zodErrors);

				return {
					...prev,
					[key]: value,
				};
			});
		},
		[onChangeValidationErrors],
	);

	const onInitInvitationCompanySignUpForm = useCallback(() => {
		setInvitationCompanySignUpForm(DEFAULT_VALUES);
		onChangeValidationErrors(undefined);
	}, [onChangeValidationErrors]);

	return {
		invitationCompanySignUpForm,
		errors,
		passwordErrorMessage: newPassword.errorMessage,
		disabled,

		onChangeInvitationCompanySignUpForm,
		onInitInvitationCompanySignUpForm,
	};
};
